/**
 * 6-DOF rigid body (non-negotiable #2: semi-implicit Euler at a fixed 120 Hz).
 *
 * ## State layout
 *
 * - `positionNed` — world position [m], NED, `z` down. Altitude is `−z`.
 * - `velocityNed` — world velocity [m/s], NED.
 * - `attitude` — quaternion rotating **body → NED**.
 * - `angularVelocityBody` — body rates `(p, q, r)` [rad/s] about `(x, y, z)` = (roll, pitch, yaw).
 *
 * Linear velocity is kept in the *world* frame and angular velocity in the *body* frame. That
 * split is deliberate: world-frame linear velocity means the translational equation is just
 * `a = F/m + g`, with no `ω × v` transport term to get wrong, while body-frame angular velocity
 * is the frame the inertia tensor is constant in, which is what makes the rotational equation
 * tractable at all.
 *
 * ## Integration
 *
 * Semi-implicit (symplectic) Euler: velocities are updated from the accelerations first, then
 * positions from the *new* velocities. At 120 Hz this is stable for everything a light
 * aeroplane does, and unlike explicit Euler it does not pump energy into oscillations —
 * a phugoid stays a phugoid instead of slowly diverging.
 *
 * The rotational equation is Euler's:
 *
 *     I·ω̇ + ω × (I·ω) = M
 *
 * The gyroscopic term `ω × (I·ω)` is what makes a spinning aeroplane precess rather than just
 * rotate, and it is why a spin has its own dynamics rather than being a steady turn.
 *
 * ## Why the angular update is not plain explicit Euler
 *
 * Evaluating the gyroscopic term at the *old* rates is unstable for a freely tumbling body:
 * angular momentum grows every step. Measured here, a body tumbling at ~4 rad/s gained 5 % of
 * its angular momentum and 14 % of its rotational energy in twenty seconds. In flight that
 * means a spin that accelerates on its own and never recovers, and a tumble that eventually
 * overflows — which is exactly what the first version of this file did.
 *
 * The fix is the **implicit midpoint rule**, iterated by fixed point. Midpoint is symplectic
 * and preserves quadratic invariants, so torque-free rotation conserves |I·ω| to round-off.
 * Two iterations are enough: the residual scales with `(ω·dt)³`, which at 120 Hz and 4 rad/s
 * is about 4 × 10⁻⁵ rad/s per step.
 *
 * The attitude increment then uses the **exponential map** — an exact finite rotation of
 * `|ω|·dt` about `ω` — rather than a first-order `q̇` step. It costs one sine and one cosine
 * and removes the normalisation drift entirely, which matters when the aircraft is tumbling at
 * several radians per second.
 *
 * ## Allocation
 *
 * Every step runs through module-level scratch vectors. `step()` allocates nothing.
 */

import { Quaternion, Vector3 } from 'three';
import { GRAVITY } from './atmosphere.js';

/** Serialisable rigid-body state — plain numbers, ready for a network packet or a replay. */
export interface RigidBodyStateData {
  positionNed: [number, number, number];
  velocityNed: [number, number, number];
  /** Attitude quaternion as `[x, y, z, w]`. */
  attitude: [number, number, number, number];
  angularVelocityBody: [number, number, number];
}

/** Mass properties. The inertia tensor is body-fixed and assumed symmetric. */
export interface MassProperties {
  /** Mass [kg]. */
  massKg: number;
  /** Roll inertia about the body x axis [kg·m²]. */
  ixx: number;
  /** Pitch inertia about the body y axis [kg·m²]. */
  iyy: number;
  /** Yaw inertia about the body z axis [kg·m²]. */
  izz: number;
  /**
   * Roll–yaw product of inertia [kg·m²]. Non-zero whenever the principal axes are not the
   * body axes, which is the usual case; it is what couples roll rate into yaw acceleration.
   */
  ixz: number;
}

const scratchA = /* #__PURE__ */ new Vector3();
const scratchB = /* #__PURE__ */ new Vector3();
const scratchC = /* #__PURE__ */ new Vector3();
const scratchMid = /* #__PURE__ */ new Vector3();
const scratchNext = /* #__PURE__ */ new Vector3();
const scratchQuat = /* #__PURE__ */ new Quaternion();
const scratchIncrement = /* #__PURE__ */ new Quaternion();

/**
 * Fixed-point iterations for the implicit midpoint angular update. Two is the knee of the
 * curve: one leaves visible momentum drift in a tumble, three is indistinguishable from two.
 */
const MIDPOINT_ITERATIONS = 2;

export class RigidBody {
  /** World position [m], NED. */
  readonly positionNed = new Vector3();
  /** World velocity [m/s], NED. */
  readonly velocityNed = new Vector3();
  /** Attitude, rotating body → NED. */
  readonly attitude = new Quaternion();
  /** Body rates `(p, q, r)` [rad/s]. */
  readonly angularVelocityBody = new Vector3();

  /** Body-frame acceleration from the last step [m/s²], gravity excluded — what an accelerometer reads. */
  readonly specificForceBody = new Vector3();

  private massKgValue = 1;
  private ixx = 1;
  private iyy = 1;
  private izz = 1;
  private ixz = 0;

  /** Inverse inertia tensor, precomputed. Symmetric with only the xz product, so 4 terms. */
  private invIxx = 1;
  private invIzz = 1;
  private invIxz = 0;
  private invIyy = 1;

  constructor(mass?: MassProperties) {
    if (mass !== undefined) this.setMassProperties(mass);
  }

  get massKg(): number {
    return this.massKgValue;
  }

  /**
   * Set mass and inertia, and precompute the inverse inertia tensor.
   *
   * With only `Ixz` off-diagonal, the tensor is block-diagonal: the (x, z) pair inverts as a
   * 2×2 and `Iyy` inverts on its own. Doing it in closed form keeps the per-tick cost to a few
   * multiplies and avoids a general 3×3 inversion in the hot path.
   */
  setMassProperties(mass: MassProperties): void {
    if (!(mass.massKg > 0)) throw new RangeError('RigidBody: mass must be > 0');
    if (!(mass.ixx > 0) || !(mass.iyy > 0) || !(mass.izz > 0)) {
      throw new RangeError('RigidBody: principal inertias must be > 0');
    }
    const determinant = mass.ixx * mass.izz - mass.ixz * mass.ixz;
    if (!(determinant > 0)) {
      throw new RangeError('RigidBody: Ixz is too large — the inertia tensor is not positive definite');
    }

    this.massKgValue = mass.massKg;
    this.ixx = mass.ixx;
    this.iyy = mass.iyy;
    this.izz = mass.izz;
    this.ixz = mass.ixz;

    this.invIxx = mass.izz / determinant;
    this.invIzz = mass.ixx / determinant;
    this.invIxz = mass.ixz / determinant;
    this.invIyy = 1 / mass.iyy;
  }

  /** `I · ω` for a body-frame vector. Writes into `out`; allocation-free. */
  applyInertia(omega: Vector3, out: Vector3): Vector3 {
    return out.set(
      this.ixx * omega.x - this.ixz * omega.z,
      this.iyy * omega.y,
      this.izz * omega.z - this.ixz * omega.x,
    );
  }

  /** `I⁻¹ · v` for a body-frame vector. Writes into `out`; allocation-free. */
  applyInverseInertia(vector: Vector3, out: Vector3): Vector3 {
    return out.set(
      this.invIxx * vector.x + this.invIxz * vector.z,
      this.invIyy * vector.y,
      this.invIxz * vector.x + this.invIzz * vector.z,
    );
  }

  /** Altitude above the NED datum [m]. */
  get altitudeM(): number {
    return -this.positionNed.z;
  }

  /** Speed over the ground, including any vertical component [m/s]. */
  get groundSpeedMps(): number {
    return Math.hypot(this.velocityNed.x, this.velocityNed.y);
  }

  /**
   * Rotate a world-frame vector into the body frame. Writes into `out`; allocation-free.
   * `out` may alias `worldVector`.
   */
  worldToBody(worldVector: Vector3, out: Vector3): Vector3 {
    scratchQuat.copy(this.attitude).conjugate();
    return out.copy(worldVector).applyQuaternion(scratchQuat);
  }

  /** Rotate a body-frame vector into the world frame. Allocation-free. */
  bodyToWorld(bodyVector: Vector3, out: Vector3): Vector3 {
    return out.copy(bodyVector).applyQuaternion(this.attitude);
  }

  /**
   * Advance one fixed step.
   *
   * @param forceBody   Total external force in body axes [N], **excluding gravity**.
   * @param momentBody  Total external moment about the centre of gravity in body axes [N·m].
   * @param dtSeconds   Fixed timestep [s].
   */
  step(forceBody: Vector3, momentBody: Vector3, dtSeconds: number): void {
    // --- linear ---------------------------------------------------------------------
    // Specific force is what the airframe feels; gravity is added separately because an
    // accelerometer in free fall reads zero.
    const invMass = 1 / this.massKgValue;
    this.specificForceBody.copy(forceBody).multiplyScalar(invMass);

    const accelerationWorld = this.bodyToWorld(this.specificForceBody, scratchA);
    accelerationWorld.z += GRAVITY; // +z is down in NED

    this.velocityNed.addScaledVector(accelerationWorld, dtSeconds);
    // Semi-implicit: position uses the *updated* velocity.
    this.positionNed.addScaledVector(this.velocityNed, dtSeconds);

    // --- angular: implicit midpoint, ω̇ = I⁻¹ (M − ω × (I ω)) ------------------------
    const omega = this.angularVelocityBody;
    scratchNext.copy(omega); // first guess for ω at the end of the step

    for (let iteration = 0; iteration < MIDPOINT_ITERATIONS; iteration++) {
      scratchMid.copy(omega).add(scratchNext).multiplyScalar(0.5);

      const angularMomentum = this.applyInertia(scratchMid, scratchB);
      const gyroscopic = scratchC.copy(scratchMid).cross(angularMomentum);
      gyroscopic.set(
        momentBody.x - gyroscopic.x,
        momentBody.y - gyroscopic.y,
        momentBody.z - gyroscopic.z,
      );
      const angularAcceleration = this.applyInverseInertia(gyroscopic, scratchB);

      scratchNext.copy(omega).addScaledVector(angularAcceleration, dtSeconds);
    }
    omega.copy(scratchNext);

    // --- attitude: exponential map of the updated body rates -------------------------
    const rate = omega.length();
    const q = this.attitude;
    if (rate > 1e-12) {
      const halfAngle = 0.5 * rate * dtSeconds;
      const scale = Math.sin(halfAngle) / rate;
      scratchIncrement.set(
        omega.x * scale,
        omega.y * scale,
        omega.z * scale,
        Math.cos(halfAngle),
      );
      // A body-frame increment post-multiplies the body → world attitude.
      q.multiply(scratchIncrement);
      // The exponential map is unit by construction; this only cleans up round-off.
      q.normalize();
    }
  }

  /** Load-factor magnitude in g, along the body vertical — what the accelerometer shows. */
  get loadFactor(): number {
    return -this.specificForceBody.z / GRAVITY;
  }

  /** Snapshot the state into a plain struct. Allocates — not for the hot path. */
  serialise(): RigidBodyStateData {
    return {
      positionNed: [this.positionNed.x, this.positionNed.y, this.positionNed.z],
      velocityNed: [this.velocityNed.x, this.velocityNed.y, this.velocityNed.z],
      attitude: [this.attitude.x, this.attitude.y, this.attitude.z, this.attitude.w],
      angularVelocityBody: [
        this.angularVelocityBody.x,
        this.angularVelocityBody.y,
        this.angularVelocityBody.z,
      ],
    };
  }

  /** Restore a snapshot taken by {@link serialise}. */
  deserialise(data: RigidBodyStateData): void {
    this.positionNed.fromArray(data.positionNed);
    this.velocityNed.fromArray(data.velocityNed);
    this.attitude.set(data.attitude[0], data.attitude[1], data.attitude[2], data.attitude[3]);
    this.attitude.normalize();
    this.angularVelocityBody.fromArray(data.angularVelocityBody);
  }
}
